import usePermissions from "@/hooks/use-permissions.hook";
import { useWorkflowsByIds, useWorkflowsSearchInline } from "@/queries/workflows/workflow.query";
import { ApiWorkflow, ApiWorkflowListItem } from "@/types/workflows/workflow.types";
import { ApiFeatureType } from "@/types/subscription.types";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types";
import { Select, Stack, Switch, Text, Title } from "@skailer-com/uikit";
import { WorkflowSelectedListItem } from "@/pages/workflows/components/WorkflowSelectedListItem.tsx";
import { useTranslation } from "react-i18next";
import { useCanLaunchHireWorkflows, useHireContext } from "../hire-context";

const toListItem = (workflow: ApiWorkflow): ApiWorkflowListItem => ({
  ...workflow,
  runs_count: 0,
  datetime_last_run_at: null,
  trigger_event: null,
  trigger_schedule_rule: null,
  trigger_recurring_rule: null,
  trigger_conditions: [],
  action_count: 0,
  actions: [],
  edges: [],
  start_block_id: null,
});

function SelectedWorkflow({
  workflow,
  locked,
}: {
  workflow: ApiWorkflowListItem;
  locked: boolean;
}) {
  const { onboarding, setOnboarding, isSubmitting } = useHireContext();
  const { canCurrentEmployeeEditField } = usePermissions();
  const id = workflow.id;
  const item = onboarding.workflows.find((workflow) => workflow.workflow_id === id)!;
  return (
    <WorkflowSelectedListItem
      workflow={workflow}
      isBlocking={item.block_portal_access}
      disabled={isSubmitting || !onboarding.workflows_enabled}
      onBlockingChange={
        canCurrentEmployeeEditField(EmployeeFieldPermission.employee_portal_access)
          ? (_, block_portal_access) =>
              setOnboarding({
                ...onboarding,
                workflows: onboarding.workflows.map((workflow) =>
                  workflow.workflow_id === id ? { ...workflow, block_portal_access } : workflow
                ),
              })
          : undefined
      }
      onRemove={
        !locked
          ? () =>
              setOnboarding({
                ...onboarding,
                workflows: onboarding.workflows.filter((workflow) => workflow.workflow_id !== id),
              })
          : undefined
      }
    />
  );
}

export function HireWorkflowsSection() {
  const { t } = useTranslation();
  const { flow, onboarding: value, setOnboarding, isSubmitting } = useHireContext();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const canSearch =
    isFeatureEnabled(ApiFeatureType.workflows) && canIEdit(ApiPermission.workflow_module);
  const canLaunch = useCanLaunchHireWorkflows() && canSearch;
  const canGetById = canSearch && canIEdit(ApiPermission.workflows_create);
  const selectedWorkflows = useWorkflowsByIds(
    value.workflows.map((item) => item.workflow_id),
    canGetById
  );
  const { items, search, isFetching } = useWorkflowsSearchInline(canSearch);
  const options = items.filter(
    (item) =>
      !item.is_shared && !value.workflows.some((selected) => selected.workflow_id === item.id)
  );
  return (
    <Stack className="card-panel" gap="2xl">
      <Title header={t("workflow:hire_workflows_title")} />
      <Switch
        title={t("workflow:hire_launch_workflows")}
        description={t("workflow:hire_launch_workflows_hint")}
        checked={value.workflows_enabled}
        disabled={isSubmitting || flow.workflows_is_mandatory}
        onChange={(workflows_enabled) => setOnboarding({ ...value, workflows_enabled })}
      />
      {value.workflows_enabled && (
        <Stack gap="md">
          <Text className="text-secondary">{t("workflow:hire_blocking_hint")}</Text>
          {canLaunch && !flow.workflows_locked && (
            <Select
              label={t("workflow:hire_choose_workflow")}
              emptyTitle={t("workflow:hire_choose_workflow")}
              value={undefined}
              options={options}
              searchOptions={options}
              searchable
              onSearchOptions={search}
              isLoading={isFetching}
              disabled={isSubmitting || !value.workflows_enabled}
              onChange={(id) => {
                if (id != null) {
                  setOnboarding({
                    ...value,
                    workflows: [
                      ...value.workflows,
                      { workflow_id: Number(id), block_portal_access: false },
                    ],
                  });
                  search(undefined);
                }
              }}
            />
          )}
          <Stack>
            {value.workflows.map((item, index) => {
              const loaded = selectedWorkflows[index]?.data;
              const workflow =
                items.find((workflow) => workflow.id === item.workflow_id) ??
                (loaded ? toListItem(loaded) : undefined);
              return workflow ? (
                <SelectedWorkflow
                  key={item.workflow_id}
                  workflow={workflow}
                  locked={flow.workflows_locked || !value.workflows_enabled}
                />
              ) : null;
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
